import { useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import classNames from 'classnames'
import {
  Actions,
  type ActionItem,
  Button,
  Dropdown,
  EmptyCell,
  Icon,
  SpinnerLoader,
  Typography,
  Status,
} from 'shared/ui'
import { sameWidth } from 'shared/lib'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { PaymentMethodsStore } from './store/paymentMethodsStore'
import { type PaymentMethodRow } from './model/PaymentMethodRow'
import styles from './styles.module.scss'

const PaymentMethodRowItem = observer(
  ({
    row,
    isDragging,
    dragHandleProps,
    hideDragHandle,
    actions,
  }: {
    row: PaymentMethodRow
    isDragging: boolean
    dragHandleProps: React.HTMLAttributes<HTMLDivElement> | null | undefined
    hideDragHandle?: boolean
    actions?: ActionItem[]
  }) => {
    return (
      <div
        className={classNames(styles.tableRow, {
          [styles.dragging]: isDragging,
          [styles.noDragHandle]: hideDragHandle,
        })}
      >
        <div className={styles.methodCell}>
          <div className={styles.methodCellIconGroup}>
            {!hideDragHandle && (
              <div
                className={classNames(styles.dragHandle, {
                  [styles.dragHandleDisabled]: !dragHandleProps,
                })}
                {...dragHandleProps}
              >
                {dragHandleProps && (
                  <Icon icon='dotsDrag' fontSize={12} color='var(--content-primary-tertiary)' />
                )}
              </div>
            )}
            <Icon
              icon={row.source.icon}
              fontSize={row.source.brand ? 32 : 20}
              color='var(--content-primary-tertiary)'
              className={!row.source.brand ? styles.methodIconAch : ''}
            />
          </div>
          <Typography ariaLabel='payment_method_name' variant='body-md-regular'>
            {row.source.label} .... {row.source.last4}
          </Typography>
        </div>
        <div className={styles.expiryCell}>
          {row.source.exp ? (
            <Typography ariaLabel='payment_method_expiry' variant='body-md-regular'>
              {row.source.exp}
            </Typography>
          ) : (
            <EmptyCell />
          )}
        </div>
        <div className={styles.badgesCell}>
          {row.source.isMethodAch && (
            <Status
              title={'Verified'}
              intent={'positive'}
              emphasis={'high'}
              size={'medium'}
              icon={'check2'}
            />
          )}
          {row.source.isPrimary && (
            <Typography
              ariaLabel='payment_method_primary'
              variant='body-md-regular'
              color='var(--content-primary-primary)'
              className={styles.badge}
            >
              Primary
            </Typography>
          )}
          {row.isAutoRecharge && (
            <Typography
              ariaLabel='payment_method_auto_recharge'
              variant='body-md-regular'
              color='var(--content-primary-primary)'
              className={styles.badge}
            >
              Auto-Recharge
            </Typography>
          )}
        </div>
        <div className={styles.actionsCell}>
          {actions && actions.length > 0 && (
            <Actions
              actions={actions}
              showItems={0}
              dropdownAriaLabel='PaymentMethodActions'
              iconButtonProps={{ size: 'small' }}
              noVisibleActions
            />
          )}
        </div>
      </div>
    )
  }
)

export const PaymentMethods = observer(() => {
  const entities = useEntitiesContext()
  const store = useMemo(() => new PaymentMethodsStore(entities), [entities])

  useEffect(() => {
    return () => store.dispose()
  }, [store])

  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null)

  const firstRow = store.paymentMethodRows[0]
  const draggableRows = store.paymentMethodRows.filter(
    (row, index) => index !== 0 && row.source.brand
  )
  const achRows = store.paymentMethodRows.filter((row, index) => index !== 0 && !row.source.brand)
  const hasDnd = draggableRows.length > 1

  const orderedRowIds = useMemo(() => {
    const ids: string[] = []
    if (firstRow) ids.push(firstRow.id)
    draggableRows.forEach((r) => ids.push(r.id))
    achRows.forEach((r) => ids.push(r.id))
    return ids
  }, [firstRow, draggableRows, achRows])

  const shouldHideRowBorder = (rowId: string): boolean => {
    if (!hoveredRowId) return false
    const idx = orderedRowIds.indexOf(rowId)
    return (
      hoveredRowId === rowId ||
      (idx >= 0 && idx < orderedRowIds.length - 1 && orderedRowIds[idx + 1] === hoveredRowId)
    )
  }

  return (
    <div className={styles.paymentMethodsContainer}>
      <div className={styles.paymentMethodsHeader}>
        <div className={styles.paymentMethodsHeaderContent}>
          <div className={styles.paymentMethodsTitleWrapper}>
            <Typography ariaLabel='payment_methods_title' variant='body-lg-medium'>
              Payment methods
            </Typography>
          </div>
          <div className={styles.headerActions}>
            <Button
              text='Billing address'
              typeBtn='text'
              size='medium'
              color='var(--light-blue-60)'
              onClick={store.onBillingAddressClick}
            />
            {store.achFlowEnabled ? (
              <Dropdown
                typeButton='button'
                placement='bottom-start'
                buttonProps={{
                  text: 'Add payment method',
                  contained: 'secondary',
                  icon: 'chevronDown',
                  isRight: true,
                }}
                PopperProps={{
                  modifiers: [sameWidth()],
                }}
                items={[
                  {
                    id: 'card',
                    label: 'Card',
                    iconL: 'card',
                    onClick: store.onAddPaymentMethodClick,
                  },
                  {
                    id: 'ach',
                    label: 'ACH',
                    iconL: 'bank',
                    onClick: store.onAddBankAccountClick,
                  },
                ]}
              />
            ) : (
              <Button
                text='Add card'
                contained='secondary'
                onClick={store.onAddPaymentMethodClick}
              />
            )}
          </div>
        </div>
        <Typography
          ariaLabel='payment_methods_subtitle'
          variant='body-md-regular'
          color='var(--content-primary-tertiary)'
          className={styles.paymentMethodsSubtitle}
        >
          If your primary method fails, we&apos;ll charge the next card in the list
        </Typography>
      </div>

      {store.isLoading && (
        <div className={styles.loaderContainer}>
          <SpinnerLoader ariaLabel={'PaymentMethods'} />
        </div>
      )}

      {!store.isLoading && !store.paymentMethodRows.length && (
        <Typography
          className={styles.noPaymentsText}
          ariaLabel='no_payment_methods'
          variant='body-md-regular'
          color='var(--content-primary-tertiary)'
        >
          Add your primary payment method
        </Typography>
      )}

      {!store.isLoading && !!store.paymentMethodRows.length && (
        <div className={styles.wrapTable}>
          <div className={styles.tableHeader}>
            <Typography
              ariaLabel='method_header'
              variant='body-md-medium'
              color='var(--content-primary-tertiary)'
              className={styles.methodHeader}
            >
              Method
            </Typography>
            <Typography
              ariaLabel='expiry_header'
              variant='body-md-medium'
              color='var(--content-primary-tertiary)'
              className={styles.expiryHeader}
            >
              Expiry date
            </Typography>
            <div className={styles.badgesHeader} />
            <div className={styles.actionsHeaderSpacer} aria-hidden />
          </div>

          {firstRow && (
            <div
              className={classNames(styles.tableRowWrapper, {
                [styles.hideRowBorder]: shouldHideRowBorder(firstRow.id),
              })}
              onMouseEnter={() => setHoveredRowId(firstRow.id)}
              onMouseLeave={() => setHoveredRowId(null)}
            >
              <PaymentMethodRowItem
                key={firstRow.id}
                row={firstRow}
                isDragging={false}
                dragHandleProps={null}
                hideDragHandle={!hasDnd}
              />
            </div>
          )}

          {draggableRows.length === 1 &&
            draggableRows.map((row) => (
              <div
                key={row.id}
                className={classNames(styles.tableRowWrapper, {
                  [styles.hideRowBorder]: shouldHideRowBorder(row.id),
                })}
                onMouseEnter={() => setHoveredRowId(row.id)}
                onMouseLeave={() => setHoveredRowId(null)}
              >
                <PaymentMethodRowItem
                  row={row}
                  isDragging={false}
                  dragHandleProps={null}
                  hideDragHandle
                  actions={store.getRowActions(row.source)}
                />
              </div>
            ))}
          {draggableRows.length > 1 && (
            <DragDropContext onDragEnd={store.onDragEnd}>
              <Droppable droppableId='payment-methods'>
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {draggableRows.map((row, index) => (
                      <Draggable key={row.id} draggableId={row.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            className={classNames(styles.tableRowWrapper, {
                              [styles.hideRowBorder]: shouldHideRowBorder(row.id),
                            })}
                            {...provided.draggableProps}
                            style={provided.draggableProps.style}
                            onMouseEnter={() => setHoveredRowId(row.id)}
                            onMouseLeave={() => setHoveredRowId(null)}
                          >
                            <PaymentMethodRowItem
                              row={row}
                              isDragging={snapshot.isDragging}
                              dragHandleProps={provided.dragHandleProps}
                              actions={store.getRowActions(row.source)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}

          {achRows.map((row) => (
            <div
              key={row.id}
              className={classNames(styles.tableRowWrapper, {
                [styles.hideRowBorder]: shouldHideRowBorder(row.id),
              })}
              onMouseEnter={() => setHoveredRowId(row.id)}
              onMouseLeave={() => setHoveredRowId(null)}
            >
              <PaymentMethodRowItem
                row={row}
                isDragging={false}
                dragHandleProps={null}
                hideDragHandle={!hasDnd}
                actions={store.getAchRowActions(row.source)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
})
